import './App.css';
import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import Home from './pages/Home';
import Mission from './pages/Mission';
import CustomMold from './pages/CustomMold';
import ProductCategory from './pages/ProductCategory';
import PetProduct from './pages/PetProduct';
import GenericProduct from './pages/GenericProduct';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import { useI18n } from './i18n/LanguageContext';
import YkHomePortal from './ykhome/YkHomePortal';

const BASE_URL = 'https://www.youngerkarom.ro';

function getSeoConfig(pathname, t) {
  const defaultTitle = 'Younger Karom - Producator Flacoane PET din 1997';
  const defaultDescription =
    'Din 1997, ambalaje din plastic pentru parteneriate durabile. Producem sticle PET și oferim o gamă completă de produse conexe pentru industria ambalajelor.';

  const byPath = [
    {
      match: (p) => p.startsWith('/produse'),
      title: 'Produse - Younger Karom',
      description: 'FLACOANE, DOPURI, MÂNERE, PREFORME, CASEROLE, GĂLEȚI, CANISTRE.',
    },
    {
      match: (p) => p === '/matrita-personalizata',
      title: 'Sticla Ta Unica! - Younger Karom',
      description: 'Îți creăm designul personalizat gratuit, în funcție de cerințele tale tehnice și de brand.',
    },
    {
      match: (p) => p === '/misiunea-noastra',
      title: 'Misiunea Noastră - Younger Karom',
      description:
        'Roadmap-ul nostru: etapele importante din evoluția Younger Karom, de la începuturi până în prezent.',
    },
    {
      match: (p) => p === '/contact',
      title: 'Contact - Younger Karom',
      description: 'Strada Paharnicul Turturea 12, Sector 2, București, România. Telefon Vânzări: +40 722 256 339.',
    },
    {
      match: (p) => p === '/politica-confidentialitate',
      title: 'Politica de confidentialitate - Younger Karom',
      description: 'Informatii despre datele prelucrate prin site, scopuri, servicii terte si drepturile vizitatorilor.',
    },
    {
      match: (p) => p === '/politica-cookie-uri',
      title: 'Politica de cookie-uri - Younger Karom',
      description: 'Informatii despre cookies necesare, analytics optionale si gestionarea consimtamantului pe site.',
    },
  ];

  const matched = byPath.find((entry) => entry.match(pathname));

  return {
    title: matched?.title || defaultTitle,
    description: matched?.description || defaultDescription,
    canonical: pathname === '/' ? `${BASE_URL}/` : `${BASE_URL}${pathname}`,
    locale: t('common.language') === 'English' ? 'en_US' : 'ro_RO',
  };
}

function prettifySlug(value) {
  return String(value)
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getBreadcrumbSchema(pathname, t) {
  if (pathname === '/') return null;

  const categoryLabels = {
    'sticle-pet': t('products.items.petBottles.title'),
    'capace-manere': t('products.items.capsHandles.title'),
    preforme: t('products.items.preforms.title'),
    'galeti-bidoane': t('products.items.bucketsGallons.title'),
  };

  const items = [{ name: t('nav.home'), path: '/' }];

  if (pathname === '/misiunea-noastra') {
    items.push({ name: t('nav.mission'), path: pathname });
  } else if (pathname === '/matrita-personalizata') {
    items.push({ name: t('nav.customMold'), path: pathname });
  } else if (pathname === '/contact') {
    items.push({ name: t('nav.contact'), path: pathname });
  } else if (pathname === '/politica-confidentialitate') {
    items.push({ name: t('legal.privacyPolicy'), path: pathname });
  } else if (pathname === '/politica-cookie-uri') {
    items.push({ name: t('legal.cookiePolicy'), path: pathname });
  } else if (pathname.startsWith('/produse')) {
    const segments = pathname.split('/').filter(Boolean);
    items.push({ name: t('nav.products'), path: '/produse/sticle-pet' });

    const category = segments[1];
    const productId = segments[2];

    if (category && categoryLabels[category]) {
      items.push({ name: categoryLabels[category], path: `/produse/${category}` });
    }

    if (productId) {
      items.push({ name: prettifySlug(productId), path: pathname });
    }
  }

  if (items.length < 2) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.path === '/' ? `${BASE_URL}/` : `${BASE_URL}${item.path}`,
    })),
  };
}

function SeoStructuredData() {
  const { pathname } = useLocation();
  const { t } = useI18n();

  const schemas = React.useMemo(() => {
    const organization = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${BASE_URL}/#organization`,
      name: 'YOUNGER KAROM PRODUCTION COMPANY SRL',
      alternateName: 'Younger Karom',
      url: `${BASE_URL}/`,
      logo: `${BASE_URL}/brand/yk-logo.png`,
      email: 'office@youngerkarom.ro',
      telephone: '+40 722 256 339',
      identifier: [
        {
          '@type': 'PropertyValue',
          name: 'CUI',
          value: '4219179',
        },
        {
          '@type': 'PropertyValue',
          name: 'Registrul Comertului',
          value: 'J40/15256/1993',
        },
      ],
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Strada Paharnicul Turturea 12',
        addressLocality: 'Bucuresti',
        addressCountry: 'RO',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        telephone: '+40 722 256 339',
        email: 'office@youngerkarom.ro',
        areaServed: 'RO',
        availableLanguage: ['ro', 'en'],
      },
    };

    const website = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${BASE_URL}/#website`,
      url: `${BASE_URL}/`,
      name: 'Younger Karom',
      inLanguage: 'ro-RO',
      publisher: {
        '@id': `${BASE_URL}/#organization`,
      },
    };

    const breadcrumb = getBreadcrumbSchema(pathname, t);
    return [organization, website, breadcrumb].filter(Boolean);
  }, [pathname, t]);

  return schemas.map((schema, index) => (
    <script
      key={`${schema['@type']}-${index}`}
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  ));
}

function ScrollToTop() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function SeoMeta() {
  const { pathname } = useLocation();
  const { lang, t } = useI18n();

  React.useEffect(() => {
    const seo = getSeoConfig(pathname, t);

    document.title = seo.title;
    document.documentElement.lang = lang;

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', seo.description);
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', seo.canonical);
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', seo.canonical);
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', seo.title);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', seo.description);
    }

    const ogLocale = document.querySelector('meta[property="og:locale"]');
    if (ogLocale) {
      ogLocale.setAttribute('content', seo.locale);
    }

    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', seo.title);
    }

    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', seo.description);
    }
  }, [lang, pathname, t]);

  return null;
}

function PublicWebsite() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/misiunea-noastra" element={<Mission />} />
        <Route path="/matrita-personalizata" element={<CustomMold />} />
        <Route path="/produse" element={<Navigate to="/produse/sticle-pet" replace />} />
        <Route path="/produse/sticle-pet/:productId" element={<PetProduct />} />
        <Route path="/produse/:category/:productId" element={<GenericProduct />} />
        <Route path="/produse/:category" element={<ProductCategory />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/politica-confidentialitate" element={<PrivacyPolicy />} />
        <Route path="/politica-cookie-uri" element={<CookiePolicy />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <SeoMeta />
      <SeoStructuredData />
      <Routes>
        <Route path="/ykhome/*" element={<YkHomePortal />} />
        <Route path="*" element={<PublicWebsite />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
